import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';

import React, { useMemo } from 'react';

import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { Text, TextType } from 'ui-components/components/text';

import { localizedPlanNameMessageDescriptor } from '../../../plan-name-helper';
import { getPlanHighlightsMessages } from './plan-highlights-message-map';

import { messages } from './messages';

import styles from './plan-highlights.module.less';

/**
 * @param {object} props - Component props
 * @param {string} props.billingPlanId - The ID of the plan
 * @param {boolean} props.isCurrentPlan - Whether this is the current plan
 * @param {string} props.planName - The name of the plan being highlighted
 * @param {string} props.previousTierPlanName - The name of the previous tier plan
 * @returns {React.JSX.Element|null} The rendered component or null if no highlights are available
 */
export const PlanHighlights = ({ billingPlanId, isCurrentPlan, planName, previousTierPlanName }) => {
  const intl = useIntl();

  const headerMessage = useMemo(() => {
    if (!previousTierPlanName) {
      return intl.formatMessage(messages.defaultHighlightsHeader, { planName });
    }

    const { messageDescriptor, values } = localizedPlanNameMessageDescriptor(previousTierPlanName);
    const formattedPreviousTierPlanName = intl.formatMessage(messageDescriptor, values);

    return intl.formatMessage(messages.higherTierHighlightsHeader, {
      previousTierPlanName: formattedPreviousTierPlanName,
    });
  }, [intl, previousTierPlanName, planName]);

  const featureMessages = getPlanHighlightsMessages(billingPlanId);
  if (!featureMessages) {
    return null;
  }

  return (
    <div
      className={classnames(styles['plan-highlights'], {
        [styles['is-current-plan']]: isCurrentPlan,
      })}
    >
      <Text className={styles['summary-title']} textType={TextType.Label} value={headerMessage} />
      <ul className={styles['feature-list']}>
        {featureMessages.map((feature) => {
          return (
            <li key={feature.id} className={styles['feature-summary-list-item']}>
              {previousTierPlanName ? (
                <FontAwesomeIcon className={styles['small-checkmark']} icon={faCheck} />
              ) : (
                <FontAwesomeIcon className={styles['small-circle']} icon={faCircle} />
              )}
              <Text className={styles['feature-summary-list-item-text']} textType={TextType.Caption} value={feature} />
            </li>
          );
        })}
      </ul>
    </div>
  );
};
